'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useQueryClient } from '@tanstack/react-query'
import { styled } from 'styled-components'

import {
  useAccessToken,
  useDecodedAccessToken,
} from '@b-stock/fe-core/bstock-next/components'
import { useFlag } from '@b-stock/fe-core/bstock-next/helpers'
import {
  NestableAnalyticsProvider,
  noop,
  FormattedMessage,
} from '@b-stock/fe-core/bstock-react'

import { AccordionCard } from '@/buyer-portal/components/layouts/CardLayout/card/AccordionCard'
import Carousel, {
  CarouselItemWrapper,
} from '@/components/common/carousel/Carousel'
import { FeatureFlags } from '@/helpers/featureFlags'
import { ItemListProvider } from '@/helpers/telemetry/itemListContext'
import { ScreenName } from '@/helpers/telemetry/ScreenName'
import { useAnalytics } from '@/helpers/telemetry/SegmentAnalytics'
import listingSearchQuery, {
  sortListingsByIdOrder,
} from '@/queries/listingSearchQuery'
import type { SearchResultsPage } from '@/queries/listingSearchQuery'
import { viewedListingsQuery } from '@/queries/viewedListingsQuery'

import ListingCard from '../listingCard/ListingCard'
import ListingCardSkeleton from '../listingCard/ListingCardSkeleton'
import { defaultState } from '../search/ListingSearchProvider/defaultState'
import ListingSearchContext from '../search/ListingSearchProvider/ListingSearchContext'

// container-type enables cqw on CardWrapper. padding matches MoreFromSellerCard
// Body (1rem). Gap override reduces Carousel's default 1.5rem to 1rem.
const CarouselWrapper = styled.div`
  container-type: inline-size;
  padding: calc(1rem - 0.125rem) calc(1rem - 0.3125rem); // subtract carousel padding to get real 1rem

  ${CarouselItemWrapper} > div {
    display: grid; // stretch each slot to the row's tallest so card bottoms align
    margin-right: 1rem;
  }
`

// Each card slot = card width + 1rem gap. CarouselItemContainer clips at
// exactly 100cqw (flex shrink absorbs the 2 × 0.875rem button gutters).
// So N exact cards show when width = 100cqw/N - 1rem. Resulting card sizes:
//   1 col  (<29.5rem):  100cqw − 1rem  (e.g. ~27.5rem at 28.5rem container)
//   2 cols (≥29.5rem):   50cqw − 1rem  = 13.75rem at breakpoint
//   3 cols (≥44.75rem): 33cqw  − 1rem  ≈ 13.92rem at breakpoint
// Breakpoints match MoreFromSeller's minmax(14.25rem,1fr) grid transitions;
// padding now equals MoreFromSellerCard Body (both 1rem) so no offset needed.
const CardWrapper = styled.div`
  display: grid; // fill the stretched slot so the card matches the row height
  min-width: 0;
  width: calc(100cqw - 1rem);

  @container (min-width: 29.5rem) {
    width: calc(50cqw - 1rem);
  }

  @container (min-width: 44.75rem) {
    width: calc(33.333cqw - 1rem);
  }
`

const ITEM_LIST_ID = 'carousel-recently-viewed-pdp'
const ITEM_LIST_NAME = 'Recently Viewed'

export function RecentlyViewedCarousel({
  excludeListingId,
}: {
  excludeListingId: string
}) {
  const isEnabled = useFlag(FeatureFlags.ENABLE_RECENTLY_VIEWED_CAROUSEL)
  const decoded = useDecodedAccessToken()
  const personId = decoded?.personId
  const accessToken = useAccessToken()
  const queryClient = useQueryClient()
  const { track } = useAnalytics()

  const { data: viewedIds = [], isLoading: isLoadingIds } =
    viewedListingsQuery.useWithOpts([personId ?? ''], {
      enabled: isEnabled && !!personId,
    })

  const filteredIds = useMemo(
    () => viewedIds.filter((id) => id !== excludeListingId).slice(0, 12),
    [viewedIds, excludeListingId]
  )

  const searchParams = useMemo(
    () => ({
      params: { id: filteredIds, limit: 12 },
      usePostEndpoint: true as const,
      fetchFilters: false,
    }),
    [filteredIds]
  )

  const { data: searchData, isLoading: isLoadingSearch } =
    listingSearchQuery.useWithOpts([searchParams], {
      enabled: isEnabled && !!personId && filteredIds.length > 0,
    })

  const isLoading = isLoadingIds || (filteredIds.length > 0 && isLoadingSearch)
  const listings = useMemo(
    () => sortListingsByIdOrder(searchData?.listings ?? [], filteredIds),
    [searchData, filteredIds]
  )

  const impressionTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const [currentImpressionId, setCurrentImpressionId] = useState<string | null>(
    null
  )

  useEffect(() => {
    if (!isLoading && listings.length > 0) {
      if (impressionTimeoutRef.current)
        clearTimeout(impressionTimeoutRef.current)
      impressionTimeoutRef.current = setTimeout(() => {
        const impressionId = crypto.randomUUID()
        setCurrentImpressionId(impressionId)
        track('listing_card_impression', {
          listing_card_impression_id: impressionId,
          listings_count: listings.length,
          carousel_name: 'recently-viewed',
          screen_name: ScreenName.ListingDetails,
          listings: listings.map((l, i) => ({ id: l.id, position: i + 1 })),
        })
      }, 3000)
    }
    return () => {
      if (impressionTimeoutRef.current)
        clearTimeout(impressionTimeoutRef.current)
    }
  }, [isLoading, listings, track])

  const updateSearchData = useCallback(
    (updater: (data: SearchResultsPage) => SearchResultsPage) => {
      queryClient.setQueryData<SearchResultsPage>(
        listingSearchQuery.getKey(accessToken, [searchParams]),
        (oldData) => (oldData ? updater(oldData) : undefined)
      )
    },
    [searchParams, accessToken, queryClient]
  )

  const contextValue = useMemo(
    () => ({
      data: searchData ?? null,
      getActiveFilterCount: () => 0,
      isLoading,
      resetFilters: noop,
      setPage: noop,
      setPageSize: noop,
      setSavedSearch: noop,
      setSort: noop,
      setView: noop,
      setSortFrozen: noop,
      recordBidPlacedWhileFrozen: noop,
      state: { ...defaultState, page: 0, pageSize: 12 },
      updateFilters: noop,
      updateSearchData,
      error: null,
      dispatch: noop,
      currentImpressionId,
      similarSearch: { enabled: false, data: undefined, isFetching: false },
      myBidsFilter: { isLoading: false, isError: false },
      personalizationEligibility: 'pending' as const,
    }),
    [searchData, isLoading, updateSearchData, currentImpressionId]
  )

  const analyticsData = useMemo(
    () => ({
      carousel_name: 'recently-viewed',
      ...(currentImpressionId && {
        listing_card_impression_id: currentImpressionId,
      }),
    }),
    [currentImpressionId]
  )

  if (!isEnabled || !personId) return null
  if (!isLoading && listings.length === 0) return null

  return (
    <AccordionCard
      title={<FormattedMessage id="Common.Carousel.recentlyViewedTitle" />}
    >
      <CarouselWrapper>
        <NestableAnalyticsProvider value={analyticsData}>
          <ItemListProvider
            itemListId={ITEM_LIST_ID}
            itemListName={ITEM_LIST_NAME}
          >
            <ListingSearchContext.Provider value={contextValue}>
              <Carousel minItemsToShowButtons={2}>
                {isLoading && listings.length === 0
                  ? Array.from({ length: 4 }, (_, i) => (
                      <CardWrapper key={i}>
                        <ListingCardSkeleton />
                      </CardWrapper>
                    ))
                  : listings.map((listing, index) => (
                      <CardWrapper key={listing.id}>
                        <ListingCard
                          listing={listing}
                          screenName={ScreenName.ListingDetails}
                          position={index + 1}
                        />
                      </CardWrapper>
                    ))}
              </Carousel>
            </ListingSearchContext.Provider>
          </ItemListProvider>
        </NestableAnalyticsProvider>
      </CarouselWrapper>
    </AccordionCard>
  )
}
